import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Shield, Lock, Eye, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8" />
                <h1 className="text-2xl font-bold">Privacy Policy</h1>
              </div>
              <p className="text-blue-100 mt-1">Last updated: January 19, 2025</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-8 prose prose-blue dark:prose-invert max-w-none text-slate-900 dark:text-slate-100">
            <h2 className="text-2xl font-bold mb-4">Introduction</h2>
            <p className="mb-4">
              Hines Time ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our workforce management service.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Name, email address, and phone number</li>
              <li>Company information and address</li>
              <li>Profile pictures (optional)</li>
              <li>Payment information (processed securely through Stripe)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Usage Data</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Time tracking data (clock in/out times)</li>
              <li>GPS location data (for job site verification)</li>
              <li>Job information and photo documentation</li>
              <li>Toolbox meeting acknowledgments</li>
              <li>Two-factor authentication data</li>
              <li>Device information and IP addresses</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">How We Use Your Information</h2>
            <p className="mb-4">We use the collected information to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Provide and maintain the Service</li>
              <li>Process your transactions and manage billing</li>
              <li>Send you service-related emails and notifications</li>
              <li>Verify user locations for job site compliance</li>
              <li>Generate payroll and compliance reports</li>
              <li>Improve and optimize the Service</li>
              <li>Ensure workplace safety through toolbox meeting tracking</li>
              <li>Provide customer support</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li><Lock className="w-4 h-4 inline mr-1" /><strong>Encryption:</strong> All data is encrypted in transit using SSL/TLS</li>
              <li><Lock className="w-4 h-4 inline mr-1" /><strong>Secure Storage:</strong> Passwords are hashed using bcrypt</li>
              <li><Lock className="w-4 h-4 inline mr-1" /><strong>Access Control:</strong> Role-based permissions limit data access</li>
              <li><Lock className="w-4 h-4 inline mr-1" /><strong>Two-Factor Authentication:</strong> Optional 2FA for enhanced security</li>
              <li><Lock className="w-4 h-4 inline mr-1" /><strong>Payment Security:</strong> We never store credit card information (handled by Stripe)</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">Data Retention</h2>
            <p className="mb-4">
              We retain your data for as long as your account is active or as needed to provide you services. You may request deletion of your account and data at any time by contacting us.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">GPS Location Data</h2>
            <p className="mb-4">
              <strong>Purpose:</strong> We collect GPS location data to verify that workers are at assigned job sites and to track work locations for payroll accuracy.
            </p>
            <p className="mb-4">
              <strong>User Control:</strong> Location data is only collected when users clock in/out or start jobs. Users must grant browser permission for location access.
            </p>
            <p className="mb-4">
              <strong>Access:</strong> Location data is only accessible to company administrators and supervisors for legitimate business purposes.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Third-Party Services</h2>
            <p className="mb-4">We use the following third-party services:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Stripe:</strong> Payment processing (see Stripe's Privacy Policy)</li>
              <li><strong>AWS SES/SNS:</strong> Email and SMS notifications (see AWS Privacy Notice)</li>
              <li><strong>OpenStreetMap:</strong> Geocoding and map display (see OpenStreetMap Privacy Policy)</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">Your Rights (GDPR Compliance)</h2>
            <p className="mb-4">If you are in the European Economic Area (EEA), you have the right to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><Eye className="w-4 h-4 inline mr-1" />Access your personal data</li>
              <li><Eye className="w-4 h-4 inline mr-1" />Correct inaccurate data</li>
              <li><Eye className="w-4 h-4 inline mr-1" />Request deletion of your data</li>
              <li><Eye className="w-4 h-4 inline mr-1" />Object to data processing</li>
              <li><Eye className="w-4 h-4 inline mr-1" />Data portability</li>
              <li><Eye className="w-4 h-4 inline mr-1" />Withdraw consent at any time</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">Cookies</h2>
            <p className="mb-4">
              We use essential cookies to maintain your session and provide the Service. We do not use tracking or advertising cookies.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">International Data Transfers</h2>
            <p className="mb-4">
              Your information may be transferred to and maintained on servers located outside of your country. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Children's Privacy</h2>
            <p className="mb-4">
              The Service is not intended for users under 18 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Changes to Privacy Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by email and by posting the new Privacy Policy on this page.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Contact Us</h2>
            <p className="mb-4">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mb-4">
              <strong>Scalevisory Inc.</strong><br />
              Email: privacy@hinestime.com<br />
              Support: support@hinestime.com<br />
              Address: Calgary, Alberta, Canada
            </p>

            <div className="mt-8 p-6 bg-green-50 dark:bg-green-950/40 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-sm text-green-900 dark:text-green-100">
                <Shield className="w-5 h-5 inline mr-2" />
                <strong>Your privacy is important to us.</strong> We are committed to protecting your personal information and being transparent about our data practices. 
                HinesTime is developed and operated by Scalevisory Inc.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
